import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, prettyJson } from '../lib/format';

interface ToolMapping {
  id: string;
  upstreamToolName: string;
  state: string;
  localOperationKey: string | null;
  snapshotChecksum: string;
  hasDiff: boolean;
  diff: unknown;
  reviewedAt: string | null;
}

interface UpstreamServer {
  id: string;
  name: string;
  url: string;
  serverName: string | null;
  serverVersion: string | null;
  protocolVersion: string | null;
  isActive: boolean;
  lastDiscoveryAt: string | null;
  providerAccount: string;
  latestDiscovery: {
    id: string;
    checksum: string;
    toolCount: number;
    discoveredAt: string;
    hasInstructions: boolean;
  } | null;
  toolMappings: ToolMapping[];
}

export function UpstreamMcpPage({ role }: { role: string }) {
  const servers = useResource<{ items: UpstreamServer[] }>('/api/admin/upstream-mcp-servers');
  const accounts = useResource<{
    items: Array<{ id: string; providerKey: string; label: string; providerType: string }>;
  }>('/api/admin/provider-accounts');
  const toast = useToast();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [form, setForm] = useState({ providerAccountId: '', name: '' });
  const [publishing, setPublishing] = useState<ToolMapping | null>(null);
  const [publishForm, setPublishForm] = useState({
    operationKey: '',
    serviceKey: '',
    title: '',
    summary: '',
    whenToUse: '',
    sideEffectClass: 'READ',
    mcpExposure: 'CATALOG_ONLY',
  });
  const [diffView, setDiffView] = useState<unknown>(null);
  const services = useResource<{ items: Array<{ serviceKey: string }> }>('/api/admin/services');
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const mcpAccounts = (accounts.data?.items ?? []).filter(
    (account) => account.providerType === 'mcp_upstream',
  );

  return (
    <>
      <PageHeader
        title="Serwery MCP upstream"
        description="Hub nie jest przekaźnikiem. Wykrywanie tworzy wersje robocze do przeglądu, a instrukcje i prompty serwera upstream nigdy nie są przekazywane dalej."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setRegisterOpen(true)}>
              Zarejestruj serwer
            </Button>
          ) : undefined
        }
      />

      <Callout tone="warn">
        Zmiana kontraktu po stronie upstreamu tworzy różnicę i nową wersję roboczą. Opublikowane
        narzędzie Hub pozostaje niezmienione do czasu ręcznego przeglądu.
      </Callout>

      {servers.loading ? (
        <Skeleton rows={4} />
      ) : (servers.data?.items ?? []).length === 0 ? (
        <Card>
          <EmptyState
            title="Brak serwerów MCP upstream"
            description="Najpierw utwórz dostawcę typu mcp_upstream i konto z poświadczeniami, potem zarejestruj serwer tutaj."
          />
        </Card>
      ) : (
        (servers.data?.items ?? []).map((server) => (
          <Card
            key={server.id}
            title={server.name}
            description={`${server.url} · konto ${server.providerAccount}`}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      const result = await api.post<{ toolCount: number; notice: string }>(
                        `/api/admin/upstream-mcp-servers/${server.id}/discover`,
                      );
                      toast.success(`Wykryto ${result.toolCount} narzędzi`, result.notice);
                      servers.reload();
                    } catch (error) {
                      const described = describeError(error);
                      toast.error(described.title, described.body);
                    }
                  }}
                >
                  Wykryj narzędzia
                </Button>
              ) : undefined
            }
            tight
          >
            <div className="ah-card-body">
              <dl className="ah-kv">
                <dt>Serwer</dt>
                <dd>
                  {server.serverName ?? '—'} {server.serverVersion ?? ''}
                </dd>
                <dt>Protokół</dt>
                <dd className="ah-mono">{server.protocolVersion ?? '—'}</dd>
                <dt>Ostatnie wykrywanie</dt>
                <dd>{formatDateTime(server.lastDiscoveryAt)}</dd>
                <dt>Instrukcje upstreamu</dt>
                <dd>
                  {server.latestDiscovery?.hasInstructions ? (
                    <Badge tone="warn">zapisane do audytu, nieprzekazywane</Badge>
                  ) : (
                    'brak'
                  )}
                </dd>
              </dl>
            </div>

            <DataTable
              rows={server.toolMappings}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  title="Brak wykrytych narzędzi"
                  description="Uruchom wykrywanie, aby pobrać migawkę."
                />
              }
              columns={[
                {
                  key: 'tool',
                  header: 'Narzędzie upstream',
                  render: (row) => <span className="ah-mono">{row.upstreamToolName}</span>,
                },
                {
                  key: 'state',
                  header: 'Stan',
                  render: (row) => (
                    <Badge
                      tone={
                        row.state === 'PUBLISHED'
                          ? 'ok'
                          : row.state === 'REJECTED'
                            ? 'danger'
                            : 'neutral'
                      }
                    >
                      {row.state}
                    </Badge>
                  ),
                },
                {
                  key: 'local',
                  header: 'Operacja Hub',
                  render: (row) => <span className="ah-mono">{row.localOperationKey ?? '—'}</span>,
                },
                {
                  key: 'diff',
                  header: 'Zmiana kontraktu',
                  render: (row) =>
                    row.hasDiff ? (
                      <Button size="sm" onClick={() => setDiffView(row.diff)}>
                        Zobacz różnicę
                      </Button>
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (row) =>
                    canWrite && row.state === 'DRAFT' ? (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setPublishing(row);
                            setPublishForm({
                              operationKey: '',
                              serviceKey: services.data?.items[0]?.serviceKey ?? '',
                              title: row.upstreamToolName,
                              summary: '',
                              whenToUse: '',
                              sideEffectClass: 'READ',
                              mcpExposure: 'CATALOG_ONLY',
                            });
                          }}
                        >
                          Zatwierdź i opublikuj
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={async () => {
                            try {
                              await api.post(`/api/admin/upstream-mcp-mappings/${row.id}/reject`);
                              toast.success('Narzędzie odrzucone');
                              servers.reload();
                            } catch (error) {
                              const described = describeError(error);
                              toast.error(described.title, described.body);
                            }
                          }}
                        >
                          Odrzuć
                        </Button>
                      </div>
                    ) : null,
                },
              ]}
            />
          </Card>
        ))
      )}

      <Modal
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        title="Zarejestruj serwer MCP upstream"
        description="Wymaga wcześniej utworzonego konta dostawcy typu mcp_upstream z poświadczeniami."
        footer={
          <>
            <Button onClick={() => setRegisterOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.providerAccountId || !form.name}
              onClick={async () => {
                try {
                  await api.post('/api/admin/upstream-mcp-servers', form);
                  toast.success('Serwer zarejestrowany');
                  setRegisterOpen(false);
                  setForm({ providerAccountId: '', name: '' });
                  servers.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Zarejestruj
            </Button>
          </>
        }
      >
        <Field label="Konto dostawcy (mcp_upstream)">
          {(id) => (
            <Select
              id={id}
              value={form.providerAccountId}
              onChange={(event) => setForm({ ...form, providerAccountId: event.target.value })}
            >
              <option value="">— wybierz —</option>
              {mcpAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.providerKey}/{account.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={publishing !== null}
        onOpenChange={(open) => !open && setPublishing(null)}
        title="Opublikuj narzędzie upstream jako operację Hub"
        description={publishing ? `Narzędzie ${publishing.upstreamToolName}` : ''}
        footer={
          <>
            <Button onClick={() => setPublishing(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={
                !publishForm.operationKey || !publishForm.serviceKey || !publishForm.summary
              }
              onClick={async () => {
                if (!publishing) return;
                try {
                  const result = await api.post<{ notice: string }>(
                    `/api/admin/upstream-mcp-mappings/${publishing.id}/publish`,
                    {
                      operationKey: publishForm.operationKey,
                      serviceKey: publishForm.serviceKey,
                      title: publishForm.title,
                      summary: publishForm.summary,
                      ...(publishForm.whenToUse ? { whenToUse: publishForm.whenToUse } : {}),
                      sideEffectClass: publishForm.sideEffectClass,
                      mcpExposure: publishForm.mcpExposure,
                    },
                  );
                  toast.success('Utworzono wersję roboczą', result.notice);
                  setPublishing(null);
                  servers.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz wersję roboczą
            </Button>
          </>
        }
      >
        <Callout tone="warn">
          Opis dostarczony przez serwer upstream jest zapisywany jako dane niezaufane. Napisz własne
          streszczenie i wskazówki — to one trafią do agenta.
        </Callout>
        <Field label="Klucz operacji Hub">
          {(id) => (
            <TextInput
              id={id}
              value={publishForm.operationKey}
              onChange={(event) =>
                setPublishForm({ ...publishForm, operationKey: event.target.value })
              }
              placeholder="partner.search"
            />
          )}
        </Field>
        <Field label="Usługa">
          {(id) => (
            <Select
              id={id}
              value={publishForm.serviceKey}
              onChange={(event) =>
                setPublishForm({ ...publishForm, serviceKey: event.target.value })
              }
            >
              <option value="">— wybierz —</option>
              {(services.data?.items ?? []).map((service) => (
                <option key={service.serviceKey} value={service.serviceKey}>
                  {service.serviceKey}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Tytuł">
          {(id) => (
            <TextInput
              id={id}
              value={publishForm.title}
              onChange={(event) => setPublishForm({ ...publishForm, title: event.target.value })}
            />
          )}
        </Field>
        <Field label="Streszczenie" hint="Własnymi słowami. To zobaczy agent.">
          {(id) => (
            <TextInput
              id={id}
              value={publishForm.summary}
              onChange={(event) => setPublishForm({ ...publishForm, summary: event.target.value })}
            />
          )}
        </Field>
        <Field label="Kiedy używać">
          {(id) => (
            <TextInput
              id={id}
              value={publishForm.whenToUse}
              onChange={(event) =>
                setPublishForm({ ...publishForm, whenToUse: event.target.value })
              }
            />
          )}
        </Field>
        <div className="ah-grid cols-2">
          <Field
            label="Klasa skutków ubocznych"
            hint="Zweryfikuj samodzielnie — adnotacje upstreamu nie są dowodem."
          >
            {(id) => (
              <Select
                id={id}
                value={publishForm.sideEffectClass}
                onChange={(event) =>
                  setPublishForm({ ...publishForm, sideEffectClass: event.target.value })
                }
              >
                <option value="READ">READ</option>
                <option value="WRITE_ADDITIVE">WRITE_ADDITIVE</option>
                <option value="WRITE_MUTATING">WRITE_MUTATING</option>
                <option value="DESTRUCTIVE">DESTRUCTIVE</option>
                <option value="EXTERNAL_COMMUNICATION">EXTERNAL_COMMUNICATION</option>
                <option value="FINANCIAL_OR_LEGAL_EFFECT">FINANCIAL_OR_LEGAL_EFFECT</option>
              </Select>
            )}
          </Field>
          <Field label="Ekspozycja MCP">
            {(id) => (
              <Select
                id={id}
                value={publishForm.mcpExposure}
                onChange={(event) =>
                  setPublishForm({ ...publishForm, mcpExposure: event.target.value })
                }
              >
                <option value="HIDDEN">HIDDEN</option>
                <option value="CATALOG_ONLY">CATALOG_ONLY</option>
                <option value="DIRECT">DIRECT</option>
              </Select>
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={diffView !== null}
        onOpenChange={(open) => !open && setDiffView(null)}
        title="Zmiana kontraktu upstream"
      >
        <Callout tone="warn">
          Opublikowana operacja Hub nie została zmieniona. Aby przyjąć nowy kontrakt, utwórz nową
          wersję operacji po przejrzeniu różnicy.
        </Callout>
        <pre className="ah-code">{prettyJson(diffView)}</pre>
      </Modal>
    </>
  );
}
